import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, CheckCircle, Star, ChevronLeft, ChevronRight, Camera } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import CostSimulator from '../components/CostSimulator';
import { getServiceById } from '../lib/services-data';
import { formatPrice } from '../lib/utils';
import axios from 'axios';
import { SEOHead, serviceSchema } from '../components/SEOHead';
import { getMainCities } from '../lib/cities-data';
import { serviceCityPath, SERVICE_CITY_CONFIG } from '../lib/service-city-data';
import { swallowWithLog } from '../lib/logger';
import { VIEWPORT_ONCE, FADE_UP_INITIAL, FADE_UP_ANIMATE, fadeUpTransition, SCALE_IN_INITIAL, SCALE_IN_ANIMATE, SCALE_IN_TRANSITION } from '../lib/motion';

const LOCAL_KEY_BY_SERVICE = {
  'entretien-menager': 'menage',
  'entretien-linge': 'menage',
  'nettoyage-vitres': 'menage',
  'global-service-domicile': 'menage',
  'espaces-verts': 'jardinage',
  'global-service-jardin': 'jardinage',
};
const SECONDARY_LOCAL_KEYS = { 'espaces-verts': ['taille-haies'], 'global-service-jardin': ['taille-haies'] };

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] } }
};

function BeforeAfterCarousel({ photos }) {
  const [index, setIndex] = useState(0);
  const [showAfter, setShowAfter] = useState(false);

  useEffect(() => {
    if (photos.length <= 1) return;
    const timer = setInterval(() => {
      setIndex(i => (i === photos.length - 1 ? 0 : i + 1));
      setShowAfter(false);
    }, 6000);
    return () => clearInterval(timer);
  }, [photos.length]);

  if (!photos || photos.length === 0) return null;
  const current = photos[index];
  const prev = () => { setIndex(i => (i === 0 ? photos.length - 1 : i - 1)); setShowAfter(false); };
  const next = () => { setIndex(i => (i === photos.length - 1 ? 0 : i + 1)); setShowAfter(false); };

  return (
    <div>
      <div className="relative rounded-2xl overflow-hidden shadow-lg bg-card border border-border">
        <div className="grid grid-cols-2 gap-0.5 bg-border">
          <div className="relative">
            <div className="absolute top-3 left-3 z-10">
              <Badge className="bg-foreground/70 text-white hover:bg-foreground/70 text-xs">Avant</Badge>
            </div>
            <img src={current.before_photo} alt="Avant" className="w-full h-[220px] sm:h-[280px] object-cover" />
          </div>
          <div className="relative">
            <div className="absolute top-3 right-3 z-10">
              <Badge className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))] text-xs">Après</Badge>
            </div>
            <img src={current.after_photo} alt="Après" className="w-full h-[220px] sm:h-[280px] object-cover" />
          </div>
        </div>
        {current.caption && (
          <div className="p-3 text-center">
            <p className="text-sm text-muted-foreground">{current.caption}</p>
          </div>
        )}
      </div>
      {photos.length > 1 && (
        <div className="flex justify-center items-center gap-4 mt-4">
          <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={prev}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex gap-1.5">
            {photos.map((_, i) => (
              <button
                key={`item-${i}`}
                onClick={() => { setIndex(i); setShowAfter(false); }}
                className={`h-2 rounded-full transition-all ${i === index ? 'bg-[hsl(var(--primary))] w-5' : 'bg-[hsl(var(--border))] w-2'}`}
              />
            ))}
          </div>
          <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={next}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

function ServiceReviewsCarousel({ reviews }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (reviews.length <= 1) return;
    const timer = setInterval(() => {
      setIndex(i => (i === reviews.length - 1 ? 0 : i + 1));
    }, 5000);
    return () => clearInterval(timer);
  }, [reviews.length]);

  if (!reviews || reviews.length === 0) return null;
  const visible = reviews.length <= 2 ? reviews : [reviews[index], reviews[(index + 1) % reviews.length]];

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {visible.map((r, i) => (
          <motion.div
            key={`${r._id}-${index}-${i}`}
            initial={FADE_UP_INITIAL}
            animate={FADE_UP_ANIMATE}
            transition={fadeUpTransition(i)}
          >
            <Card className="p-5 h-full">
              <div className="flex items-center gap-1 mb-2">
                {[1, 2, 3, 4, 5].map(s => (
                  <Star key={s} className={`h-4 w-4 ${s <= r.note ? 'fill-amber-400 text-amber-400' : 'fill-none text-gray-300'}`} />
                ))}
              </div>
              {r.titre && <h4 className="font-semibold text-sm mb-1">{r.titre}</h4>}
              <p className="text-sm text-muted-foreground leading-relaxed mb-2">« {r.commentaire} »</p>
              <span className="text-xs font-medium">{r.nom}</span>
            </Card>
          </motion.div>
        ))}
      </div>
      {reviews.length > 2 && (
        <div className="flex justify-center gap-1.5 mt-4">
          {reviews.map((_, i) => (
            <button
              key={`item-${i}`}
              onClick={() => setIndex(i)}
              className={`h-2 rounded-full transition-all ${i === index ? 'bg-[hsl(var(--primary))] w-5' : 'bg-[hsl(var(--border))] w-2'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ServiceDetailPage() {
  const { serviceId } = useParams();
  const service = getServiceById(serviceId);
  const [beforeAfterPhotos, setBeforeAfterPhotos] = useState([]);
  const [serviceReviews, setServiceReviews] = useState([]);

  useEffect(() => {
    if (!service) return;
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/service-photos/${service.id}`)
      .then(res => { if (!cancelled) setBeforeAfterPhotos(res.data.photos); })
      .catch(swallowWithLog('photos avant/après'));
    axios.get(`${BACKEND_URL}/api/reviews?limit=10`)
      .then(res => {
        if (cancelled) return;
        const filtered = res.data.reviews.filter(r => 
          r.service && service.title.toLowerCase().includes(r.service.toLowerCase().split(' ')[0])
        );
        setServiceReviews(filtered.length > 0 ? filtered : res.data.reviews.slice(0, 4));
      })
      .catch(swallowWithLog('avis service'));
    return () => { cancelled = true; };
  }, [service]);

  if (!service) return <Navigate to="/services" replace />;

  const simulatorTab = service.category === 'jardin' || service.id === 'global-service-jardin' ? 'jardinage' : 'menage';

  return (
    <div>
      <SEOHead
        title={`${service.title} — dès ${formatPrice(service.priceAfter)}/h`}
        description={`${service.description} Crédit d'impôt 50% avec avance immédiate. Tarif : ${formatPrice(service.priceBefore)}/h → ${formatPrice(service.priceAfter)}/h après crédit. Interventions dans l'Artois.`}
        path={`/services/${service.id}`}
        schema={serviceSchema(service)}
      />
      {/* Breadcrumb + Hero */}
      <section className="bg-[linear-gradient(135deg,hsla(195,20%,97%,1)_0%,hsla(182,40%,94%,1)_55%,hsla(195,20%,97%,1)_100%)] py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-foreground">Accueil</Link>
            <span>/</span>
            <Link to="/services" className="hover:text-foreground">Services</Link>
            <span>/</span>
            <span className="text-foreground font-medium">{service.title}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <motion.div initial="hidden" animate="visible" variants={fadeUp}>
              {service.isGlobal && (
                <Badge className="mb-3 bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]">Global Service - Tout inclus</Badge>
              )}
              <h1 className="font-['Space_Grotesk'] text-3xl sm:text-4xl font-semibold tracking-tight mb-4">{service.title}</h1>
              <p className="text-muted-foreground leading-relaxed mb-6">{service.description}</p>
              <div className="flex items-center gap-4 mb-6">
                <div className="px-4 py-2 rounded-xl bg-card border border-border shadow-sm">
                  <span className="text-xs text-muted-foreground block">Tarif horaire — à partir de</span>
                  <span className="text-sm line-through text-muted-foreground">{formatPrice(service.priceBefore)}/h</span>
                  <span className="text-xl font-bold text-[hsl(var(--primary))] ml-2">{formatPrice(service.priceAfter)}/h</span>
                  <span className="text-xs text-muted-foreground block">après avance immédiate</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link to="/devis">
                  <Button className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white">
                    Demander un devis <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/rendez-vous">
                  <Button variant="outline">Prendre rendez-vous</Button>
                </Link>
              </div>
            </motion.div>

            <motion.div initial={SCALE_IN_INITIAL} animate={SCALE_IN_ANIMATE} transition={SCALE_IN_TRANSITION}>
              {service.image ? (
                <div className="rounded-2xl overflow-hidden shadow-xl">
                  <img src={service.image} alt={service.title} className="w-full h-[260px] sm:h-[320px] object-cover" loading="eager" />
                </div>
              ) : (
                <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center mb-4 mx-auto lg:mx-0">
                  <service.icon className="h-5 w-5 text-[hsl(var(--primary))]" />
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Included + Benefits */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <div>
              <h2 className="font-['Space_Grotesk'] text-xl sm:text-2xl font-semibold tracking-tight mb-6">Ce qui est inclus</h2>
              <div className="space-y-3">
                {service.included.map((item, i) => (
                  <div key={`item-${i}`} className="flex items-start gap-3 p-3 rounded-lg bg-[hsl(var(--brand-mint))]/20">
                    <CheckCircle className="h-5 w-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="font-['Space_Grotesk'] text-xl sm:text-2xl font-semibold tracking-tight mb-6">Les avantages</h2>
              <div className="space-y-3">
                {service.benefits.map((item, i) => (
                  <div key={`item-${i}`} className="flex items-start gap-3 p-3 rounded-lg bg-[hsl(var(--secondary))]/60">
                    <Star className="h-5 w-5 text-[hsl(var(--accent))] flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Before/After Photos Carousel */}
      {beforeAfterPhotos.length > 0 && (
        <section className="py-12 sm:py-16 bg-[hsl(var(--secondary))]/60">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <motion.div className="text-center mb-8" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Résultats</Badge>
              <h2 className="font-['Space_Grotesk'] text-xl sm:text-2xl font-semibold tracking-tight mb-2">Avant / Après</h2>
              <p className="text-sm text-muted-foreground">Découvrez la qualité de nos interventions</p>
            </motion.div>
            <BeforeAfterCarousel photos={beforeAfterPhotos} />
          </div>
        </section>
      )}

      {/* Service Reviews Carousel */}
      {serviceReviews.length > 0 && (
        <section className={`py-12 sm:py-16 ${beforeAfterPhotos.length === 0 ? 'bg-[hsl(var(--secondary))]/60' : ''}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <motion.div className="text-center mb-8" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Témoignages</Badge>
              <h2 className="font-['Space_Grotesk'] text-xl sm:text-2xl font-semibold tracking-tight mb-2">Ce que disent nos clients</h2>
            </motion.div>
            <ServiceReviewsCarousel reviews={serviceReviews} />
            <div className="text-center mt-6">
              <Link to="/temoignages">
                <Button variant="outline" size="sm">Voir tous les avis <ArrowRight className="ml-2 h-3.5 w-3.5" /></Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Simulator */}
      <section className="py-12 sm:py-16 bg-[hsl(var(--secondary))]/60">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="font-['Space_Grotesk'] text-xl sm:text-2xl font-semibold tracking-tight mb-2">Simulez votre coût</h2>
            <p className="text-sm text-muted-foreground">Estimez votre reste à charge après crédit d'impôt 50%</p>
          </div>
          <CostSimulator preselectedTab={simulatorTab} />
        </div>
      </section>

      {/* Local SEO: city landing pages for ménage / jardinage */}
      {LOCAL_KEY_BY_SERVICE[service.id] && (
        <section className="py-12 sm:py-16" data-testid="service-city-links-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
              <div>
                <Badge className="mb-3 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Près de chez vous</Badge>
                <h2 className="font-['Space_Grotesk'] text-xl sm:text-2xl font-semibold tracking-tight mb-1">
                  {LOCAL_KEY_BY_SERVICE[service.id] === 'menage' ? 'Femme de ménage dans votre ville' : 'Jardinier dans votre ville'}
                </h2>
                <p className="text-sm text-muted-foreground">Retrouvez le détail des prestations, tarifs et questions fréquentes pour votre commune de l'Artois.</p>
              </div>
              <Link to="/services-a-domicile"><Button variant="outline" size="sm">Toutes les villes <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
            </div>
            <div className="flex flex-wrap gap-2">
              {getMainCities().map((c) => (
                <Link key={c.slug} to={serviceCityPath(LOCAL_KEY_BY_SERVICE[service.id], c)} className="px-3.5 py-1.5 rounded-full border border-border bg-card text-sm hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors" data-testid="service-city-link">
                  {LOCAL_KEY_BY_SERVICE[service.id] === 'menage' ? 'Ménage' : 'Jardinage'} à {c.name}
                </Link>
              ))}
            </div>
            {(SECONDARY_LOCAL_KEYS[service.id] || []).map((sk) => (
              <div key={sk} className="mt-6" data-testid={`service-secondary-links-${sk}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-['Space_Grotesk'] font-semibold">{SERVICE_CITY_CONFIG[sk].label} près de chez vous</h3>
                  <Link to={SERVICE_CITY_CONFIG[sk].basePath} className="text-sm text-[hsl(var(--primary))] font-medium hover:underline inline-flex items-center gap-1">Tout savoir sur la {SERVICE_CITY_CONFIG[sk].label.toLowerCase()} <ArrowRight className="h-3.5 w-3.5" /></Link>
                </div>
                <div className="flex flex-wrap gap-2">
                  {getMainCities().map((c) => (
                    <Link key={c.slug} to={serviceCityPath(sk, c)} className="px-3.5 py-1.5 rounded-full border border-border bg-card text-sm hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors">
                      {SERVICE_CITY_CONFIG[sk].shortLabel} à {c.name}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-['Space_Grotesk'] text-xl sm:text-2xl font-semibold tracking-tight mb-4">Intéressé(e) par ce service ?</h2>
          <p className="text-muted-foreground mb-6">Contactez-nous pour un devis gratuit et sans engagement. Nous vous rappelons sous 24h ouvrées.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/devis">
              <Button size="lg" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white">
                Demander un devis <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/services">
              <Button size="lg" variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" /> Voir tous les services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
